import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  Form,
  Button,
  Input,
  Spin,
  Table,
  Switch,
  Popconfirm,
  Modal,
  message,
} from "antd";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import JSZip from "jszip";
import ReactDOM from "react-dom/client";

import { BASE_URL } from "../../utils/constants";
import AdminHeader from "../../components/AdminHeader";
import { showErrorModal } from "../../components/Modal";
import { PageEditor } from "../../components/PageEditor";

import api from "../../api/api";
import {
  LinkProfile,
  LinkProfileCreate,
  UserProfile,
  LinkButton,
} from "../../models";
import { useLinkProfile } from "../../api/useLinkProfile";
import { convertTiktokLink } from "../../utils/tiktok";
import CloseCircleFilled from "@ant-design/icons/lib/icons/CloseCircleFilled";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { colorToHex } from "../../utils/helper";
import { usePlanAccess } from "../../api/PlanAccess";
import { useStoreSelection } from "../../api/StoreSelection";
import { formatLimitValue } from "../../utils/planUtils";
import { Link as RouterLink } from "react-router-dom";

const defaultButtonStyles = {
  fontSize: "1.5rem",
  color: "#000000",
  backgroundColor: "rgba(16, 185, 129, 0.5)",
};



const LinksManager: React.FC = () => {
  const qrCodeSize = 2048;

  const [editingProfile, setEditingProfile] = useState<LinkProfile>({
    name: "main",
  } as LinkProfile);

  const [previewProfile, setPreviewProfile] = useState<LinkProfile>({
    name: "main",
  } as LinkProfile);

  const [linkButtons, setLinkButtons] = useState<LinkButton[]>([]);
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [tiktokHintVisible, setTiktokHintVisible] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  // const [qrCodeSize, setQrCodeSize] = useState(2048);
  const [pageSize] = useState(8);
  const [isAdd, setIsAdd] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isButtonFormValid, setIsButtonFormValid] = useState(false);
  const [isMainLink, setIsMainLink] = useState(false);
  const [showPageEditor, setShowPageEditor] = useState(false);
  const [form] = Form.useForm();
  const [buttonform] = Form.useForm();

  const {
    page,
    setPage,
    loading,
    linkProfiles,
    getLinkProfiles,
    getLinkProfile,
    addLinkProfile,
  } = useLinkProfile();

  const {
    canAccessStores,
    canCreateLinkProfile,
    limits,
  } = usePlanAccess();
  const {
    selectedStore,
    selectedStoreId,
    loading: storeSelectionLoading,
  } = useStoreSelection();

  const tblRef: Parameters<typeof Table>[0]["ref"] = React.useRef(null);
  const canAddLink = canCreateLinkProfile(linkProfiles.length);
  const waitingForStore =
    canAccessStores && (storeSelectionLoading || selectedStoreId == null);

  // Detect orientation
  function getOrientation() {
    if (typeof window === "undefined") return "horizontal";
    return window.innerHeight > window.innerWidth ? "vertical" : "horizontal";
  }

  const [vertical, setVertical] = useState(getOrientation() === "vertical");
  useEffect(() => {
    const handleResize = () => {
      setVertical(getOrientation() === "vertical");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchLinkProfiles = (scrollToBottom: boolean = false) => {
    const storeId =
      canAccessStores && selectedStoreId != null
        ? String(selectedStoreId)
        : null;
    getLinkProfiles(storeId)
      .then((res) => {
        if (!scrollToBottom) {
          return;
        }

        const totalPages = Math.ceil(res.length / pageSize);
        setPage(totalPages);
        if (tblRef.current) {
          tblRef.current.scrollTo({ index: -1 });
        }
      })
      .catch((err) => {
        console.error("Failed to load links", err);
        message.error("Failed to load links");
      });
  };

  useEffect(() => {
    if (waitingForStore) {
      return;
    }

    fetchLinkProfiles();
  }, [selectedStoreId, waitingForStore, canAccessStores]);


  // Initialize forms from linkButtons array (aligns with backend data)
  const initializeFormsFromButtons = (buttons: LinkButton[]) => {
      form.setFieldsValue({
        id: editingProfile.id || null,
        guide_text: editingProfile.guide_text || "",
        guide_link: editingProfile.guide_link || "",
        button_1_text_color: colorToHex(editingProfile.text_color || defaultButtonStyles.color),
        button_1_background_color: colorToHex(editingProfile.background_color || defaultButtonStyles.backgroundColor),
      });

    // Set buttons 2-4 from the array (align with backend order property)
    // Button 2 = order 1, Button 3 = order 2, Button 4 = order 3
    const buttonFormValues: Record<string, string> = {};
    
    // Initialize all possible button fields to empty strings first
    for (let order = 1; order <= 3; order++) {
      const buttonNum = order + 1; // order 1 -> button_2, order 2 -> button_3, order 3 -> button_4
      buttonFormValues[`button_${buttonNum}_text`] = "";
      buttonFormValues[`button_${buttonNum}_link`] = "";
      buttonFormValues[`button_${buttonNum}_text_color`] = colorToHex(editingProfile.text_color || defaultButtonStyles.color);
      buttonFormValues[`button_${buttonNum}_background_color`] = colorToHex(editingProfile.background_color || defaultButtonStyles.backgroundColor);
    }
    
    // Then populate from backend data
    buttons?.forEach((button) => {
      if (button.order > 0 && button.order <= 3) {
        const buttonNum = button.order + 1; // order 1 -> button_2, order 2 -> button_3, order 3 -> button_4
        buttonFormValues[`button_${buttonNum}_text`] = button.text || "";
        buttonFormValues[`button_${buttonNum}_link`] = button.url || "";
        buttonFormValues[`button_${buttonNum}_text_color`] = colorToHex(button.text_color || editingProfile.text_color || defaultButtonStyles.color);
        buttonFormValues[`button_${buttonNum}_background_color`] = colorToHex(button.background_color || editingProfile.background_color || defaultButtonStyles.backgroundColor);
      }
    });
    
    buttonform.setFieldsValue(buttonFormValues);
  };

  // Rebuild linkButtons from both forms
  const rebuildLinkButtons = (buttonLayout: number) => {

    const buttonFormValues = buttonform.getFieldsValue();
    const button2Id = linkButtons.find(button => button.order === 1)?.id || null;
    const button3Id = linkButtons.find(button => button.order === 2)?.id || null;
    const button4Id = linkButtons.find(button => button.order === 3)?.id || null;

    const button2 = {
      id: button2Id,
      order: 1,
      url: buttonFormValues.button_2_link || "",
      text: buttonFormValues.button_2_text || "",
      background_color: buttonFormValues.button_2_background_color || editingProfile.background_color || defaultButtonStyles.backgroundColor,
      text_color: buttonFormValues.button_2_text_color || editingProfile.text_color || defaultButtonStyles.color,
    } as LinkButton;
    const button3 = {
      id: button3Id,
      order: 2,
      url: buttonFormValues.button_3_link || "",
      text: buttonFormValues.button_3_text || "",
      background_color: buttonFormValues.button_3_background_color || editingProfile.background_color || defaultButtonStyles.backgroundColor,
      text_color: buttonFormValues.button_3_text_color || editingProfile.text_color || defaultButtonStyles.color,
    } as LinkButton;
    const button4 = {
      id: button4Id,
      order: 3,
      url: buttonFormValues.button_4_link || "",
      text: buttonFormValues.button_4_text || "",
      background_color: buttonFormValues.button_4_background_color || editingProfile.background_color || defaultButtonStyles.backgroundColor,
      text_color: buttonFormValues.button_4_text_color || editingProfile.text_color || defaultButtonStyles.color,
    } as LinkButton;

    let buttonList: LinkButton[] = [];
    
    switch (buttonLayout) {
      case 4:
        buttonList.push(button2);
        buttonList.push(button3);
        buttonList.push(button4);
        break;
      case 3:
        buttonList.push(button2);
        buttonList.push(button3);
        break;
      case 2:
      case 1:
        buttonList.push(button2);
        break;
      case 0:
      default:
        break;
    }

    setLinkButtons(buttonList);
  };

  const applyColorToAllButtons = (
    kind: "text" | "background",
    color: string
  ) => {
    if (!color) return;

    const fieldSuffix = kind === "text" ? "text_color" : "background_color";
    const profileField =
      kind === "text" ? "text_color" : "background_color";

    form.setFieldsValue({ [`button_1_${fieldSuffix}`]: color });
    buttonform.setFieldsValue({
      [`button_2_${fieldSuffix}`]: color,
      [`button_3_${fieldSuffix}`]: color,
      [`button_4_${fieldSuffix}`]: color,
    });
    setEditingProfile((prev) => ({ ...prev, [profileField]: color }));
    rebuildLinkButtons(editingProfile.button_layout);
    message.success(
      kind === "text"
        ? "Text color applied to all buttons"
        : "Background color applied to all buttons"
    );
  };

  const renderColorPickerWithApply = (
    name: string,
    label: string,
    inputId: string,
    kind: "text" | "background",
    sourceForm: "main" | "button"
  ) => (
    <Form.Item
      style={{ margin: 0, flex: 1 }}
      label={<span style={{ fontSize: "12px" }}>{label}</span>}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Form.Item name={name} noStyle>
          <Input
            type="color"
            id={inputId}
            style={{ width: "50%", height: 40, minWidth: 48 }}
          />
        </Form.Item>
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: "auto", whiteSpace: "nowrap" }}
          onClick={() => {
            const color =
              sourceForm === "main"
                ? form.getFieldValue(name)
                : buttonform.getFieldValue(name);
            applyColorToAllButtons(kind, color);
          }}
        >
          Apply to all
        </Button>
      </div>
    </Form.Item>
  );

  // Initialize forms from linkButtons when modal opens (aligns with backend data)
  useEffect(() => {
    if (isEditModalVisible) {
      // Small delay to ensure forms are ready
      setTimeout(() => {
        initializeFormsFromButtons(linkButtons);
        // Validate both forms when modal opens
        Promise.all([
          form.validateFields().catch(() => {}),
          buttonform.validateFields().catch(() => {})
        ]).then(() => {
          // Check validation status for both forms
          const formHasError = form
            .getFieldsError()
            .some(({ errors }) => errors.length);
          const buttonFormHasError = buttonform
            .getFieldsError()
            .some(({ errors }) => errors.length);
          
          setIsFormValid(!formHasError);
          setIsButtonFormValid(!buttonFormHasError);
        });
      }, 100);
    }
  }, [isEditModalVisible]); // Initialize when modal opens

  const handleConvertTikTokLinks = async (profile: Partial<LinkProfile>) => {
    if (profile.video_link) {
      const convertedLink = await convertTiktokLink(profile.video_link);
      if (!convertedLink) {
        showErrorModal("Invalid TikTok video link");
        return null;
      } else {
        profile.video_link = convertedLink;
      }
    }

    return profile;
  };

  const handleSubmit = () => {
    var profileToSubmit: LinkProfile = editingProfile;

    if (isMainLink) {
      profileToSubmit.path = "";
    }

    if (selectedStore) {
      profileToSubmit.store_id = selectedStore.id;
    }

    profileToSubmit.buttons = linkButtons;

    if (isAdd) {
      handleAddLinkProfile(profileToSubmit);
    } else {
      handleEditProfile(profileToSubmit);
    }
  };

  const handleAddLinkProfile = async (linkProfile: LinkProfile) => {
    const convertedProfile = await handleConvertTikTokLinks(linkProfile);
    if (!convertedProfile) {
      return;
    }

    addLinkProfile(convertedProfile as LinkProfileCreate)
      .then(() => {
        fetchLinkProfiles();
        message.success("Link profile added successfully");
        setEditModalVisible(false);
      })
      .catch((err) => {
        console.error("Failed to add link profile", err);
        showErrorModal("Failed to add link profile");
      });
  };

  const handleToggleActive = async (id: number, active: boolean) => {
    try {
      await api.patch(`/v1/link_profile/${id}/`, { active: !active });
      fetchLinkProfiles();
      message.success(
        `Link ${active ? "deactivated" : "activated"} successfully`
      );
    } catch (err) {
      console.error("Failed to update link", err);
      message.error("Failed to update link status");
    }
  };

  const handleDeleteLink = async (id: number) => {
    try {
      await api.delete(`/v1/link_profile/${id}/`);
      fetchLinkProfiles();
      message.success("Link profile deleted successfully");
    } catch (err) {
      console.error("Failed to delete link", err);
      showErrorModal("Failed to delete link profile");
    }
  };

  const handleEditProfile = async (linkProfile: LinkProfile) => {
    try {
      if (!linkProfile) return;

      const convertedProfile = await handleConvertTikTokLinks(linkProfile);
      if (!convertedProfile) {
        return;
      }

      await api.put(
        `/v1/link_profile/${convertedProfile.id}/`,
        convertedProfile
      );
      setEditModalVisible(false);
      fetchLinkProfiles();
      message.success("Link profile updated successfully");
    } catch (err) {
      console.error("Failed to update link", err);
      showErrorModal("Failed to update link profile");
    }
  };

  const showQrCode = (url: string) => {
    setQrCodeUrl(url);
    setQrModalVisible(true);
  };

  const handlePreviewLink = (profile: LinkProfile) => {
    getLinkProfile(profile.id.toString())
      .then((profile) => {
        setLinkButtons(profile.buttons)
        
        setEditingProfile(profile);
        setPreviewProfile(profile);
        form.setFieldsValue(profile);
        // buttonform.setFieldsValue(profile.buttons);
        setShowPageEditor(true);
      })
      .catch((err) => {
        console.error("Failed to get link profile", err);
        message.error("failed to preview link");
      });
  };

  const handleDuplicateProfile = async (profile: LinkProfile) => {
    if (!profile) {
      message.error("profile are required");
      return;
    }
    const { id, user_profile, ...profileCopy } = profile;
    const maxId = linkProfiles ? Math.max(...linkProfiles.map((p) => p.id)) : 0;

    profileCopy.name = `${profileCopy.name} (${maxId + 1})`;
    profileCopy.path = `${profileCopy.path}-${maxId + 1}`;

    await api
      .post("/v1/link_profile/", profileCopy)
      .then(() => {
        fetchLinkProfiles();
        message.success("Link profile duplicated successfully");
      })
      .catch((err) => {
        console.error("Failed to duplicate link", err);
        showErrorModal("Failed to duplicate link profile");
      });
  };

  const generateQRCodeAsImage = async (
    qrUrl: string,
    filename: string
  ): Promise<Blob> => {
    return new Promise((resolve) => {
      // Create a temporary div to render the QR code
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      document.body.appendChild(tempDiv);

      // Render the QR code
      const qr = (
        <QRCodeCanvas
          value={qrUrl}
          size={qrCodeSize}
          level="H"
          bgColor="#ffffff"
          fgColor="#000000"
          includeMargin={true}
        />
      );

      // Use ReactDOM to render the QR code
      const root = ReactDOM.createRoot(tempDiv);
      root.render(qr);

      // Wait for the QR code to be rendered
      setTimeout(() => {
        const canvas = tempDiv.querySelector("canvas");
        if (canvas) {
          // Create a new canvas for the combined image
          const combinedCanvas = document.createElement("canvas");
          const ctx = combinedCanvas.getContext("2d");
          if (!ctx) return;

          const imgSize = qrCodeSize * 1.25;

          // Set canvas size to accommodate QR code and text
          const padding = 80; // Padding around QR code
          const textHeight = 60; // Height for text
          combinedCanvas.width = imgSize + padding * 2;
          combinedCanvas.height = imgSize + padding * 2 + textHeight;

          // Fill white background
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

          // Draw QR code
          ctx.drawImage(canvas, padding, padding);

          // Draw URL text
          ctx.fillStyle = "#333333";
          ctx.font = "48px Arial";
          ctx.textAlign = "center";

          // Split URL into multiple lines if too long
          const maxWidthQR = combinedCanvas.width - padding * 2;
          const words = qrUrl.split("/");
          let line = "";
          let y = imgSize + padding * 2 + 30; // Starting y position for text

          for (const word of words) {
            const testLine = line + word + "/";
            const metrics = ctx.measureText(testLine);

            if (metrics.width > maxWidthQR && line !== "") {
              ctx.fillText(line, combinedCanvas.width / 2, y);
              line = word + "/";
              y += 30;
            } else {
              line = testLine;
            }
          }
          ctx.fillText(line, combinedCanvas.width / 2, y);

          // Convert to blob
          combinedCanvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            }
            // Clean up
            document.body.removeChild(tempDiv);
          }, "image/png");
        }
      }, 100);
    });
  };

  const downloadAllQRCodes = async () => {
    if (!linkProfiles || linkProfiles.length === 0) {
      message.warning("No links available to generate QR codes");
      return;
    }

    try {
      message.loading({
        content: "Generating QR codes...",
        key: "qrGeneration",
      });

      const zip = new JSZip();
      const qrFolder = zip.folder("qr-codes");

      for (const profile of linkProfiles) {
        let url = getProfileURL(profile.user_profile);
        if (profile.path) {
          url += `/${profile.path}`;
        }

        const qrBlob = await generateQRCodeAsImage(
          url,
          `${profile.path || "main"}.png`
        );
        const filename = `${profile.path || "main"}.png`;
        qrFolder?.file(filename, qrBlob);
      }

      const content = await zip.generateAsync({ type: "blob" });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(content);
      downloadLink.download = "qr-codes.zip";
      downloadLink.click();

      message.success({
        content: "QR codes downloaded successfully!",
        key: "qrGeneration",
      });
    } catch (error) {
      console.error("Error generating QR codes:", error);
      message.error({
        content: "Failed to generate QR codes",
        key: "qrGeneration",
      });
    }
  };

  const getProfileURL = (profile: UserProfile) => {
    if (selectedStore) {
      return `${BASE_URL}/${selectedStore.name}`;
    }
    return `${BASE_URL}/${profile.username}`;
  };

  const columnsMain = [
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      render: (path: string, item: LinkProfile) => {
        const mainURL = getProfileURL(item.user_profile);
        const fullURL = mainURL;

        return (
          <div className="flex items-center justify-between gap-2 max-w-[400px]">
            <Button
              type="link"
              disabled={!item.active}
              icon={
                <QRCodeSVG
                  style={{ width: "28px", height: "28px" }}
                  value={fullURL}
                  size={28}
                  className="qr-code-svg"
                />
              }
              onClick={(e) => {
                e.preventDefault();
                showQrCode(fullURL);
              }}
            >
              {"(Main link)"}
            </Button>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                getLinkProfile(item.id.toString())
                  .then((profile) => {
                    const buttons = profile.buttons || [];
                    setLinkButtons(buttons);
                    setEditingProfile(profile);
                    setIsMainLink(profile.path === "");
                    form.setFieldsValue(profile);
                    // Initialize button form from backend data
                    setTimeout(() => {
                      initializeFormsFromButtons(buttons);
                    }, 0);
                  })
                  .catch((err) => {
                    console.error("error: failed to get link profile", err);
                    const buttons = item.buttons || [];
                    setLinkButtons(buttons);
                    setEditingProfile(item);
                    setIsMainLink(item.path === "");
                    form.setFieldsValue(item);
                    // Initialize button form from backend data
                    setTimeout(() => {
                      initializeFormsFromButtons(buttons);
                    }, 0);
                  })
                  .finally(() => {
                    setIsAdd(false);
                    setEditModalVisible(true);
                    rebuildLinkButtons(editingProfile.button_layout);
                  });
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: LinkProfile) => (
        <div
          style={{
            display: "flex",
            // justifyContent: "end",
            flexDirection: vertical ? "column" : "row",
          }}
        >
          <Button
            type="link"
            disabled={!record.active}
            onClick={() => {
              handlePreviewLink(record);
            }}
          >
            Preview
          </Button>
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      render: (path: string, item: LinkProfile) => {
        const mainURL = getProfileURL(item.user_profile);
        let fullURL = mainURL;
        if (path) {
          fullURL += `/${path}`;
        }

        return (
          <div className="flex items-center justify-between max-w-[400px]">
            <Button
              type="link"
              disabled={!item.active}
              icon={
                <QRCodeSVG
                  style={{ width: "28px", height: "28px" }}
                  value={fullURL}
                  size={28}
                  className="qr-code-svg"
                />
              }
              onClick={(e) => {
                e.preventDefault();
                showQrCode(fullURL);
              }}
            >
              {path ? path : "(Main link)"}
            </Button>
          </div>
        );
      },
    },
    // {
    //   title: "Full Link",
    //   dataIndex: "path",
    //   key: "id",
    //   hidden: vertical,
    //   render: (path: string, item: LinkProfile) => {
    //     const mainURL = `${BASE_URL}/${item.user_profile.username}`;
    //     let fullURL = mainURL;
    //     if (path) {
    //       fullURL += `/${path}`;
    //     }

    //     return (
    //       <div className="flex items-center justify-between gap-2 max-w-[400px]">
    //         <Button
    //           onClick={(e) => {
    //             e.preventDefault();
    //             showQrCode(fullURL);
    //           }}
    //         >
    //           Gen QR Code
    //         </Button>
    //       </div>
    //     );
    //   },
    // },
    {
      title: "Enabled",
      key: "active",
      render: (record: LinkProfile) => (
        <Switch
          checked={record.active}
          onChange={() => handleToggleActive(record.id, record.active)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: LinkProfile) => (
        <div
          style={{
            display: "flex",
            flexDirection: vertical ? "column" : "row",
            gap: 4,
          }}
        >
          {/* Edit icon button */}
          <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                getLinkProfile(record.id.toString())
                  .then((profile) => {
                    const buttons = profile.buttons || [];
                    setLinkButtons(buttons);
                    setEditingProfile(profile);
                    setIsMainLink(profile.path === "");
                    form.setFieldsValue(profile);
                    // Initialize button form from backend data
                    setTimeout(() => {
                      initializeFormsFromButtons(buttons);
                    }, 0);
                  })
                  .catch((err) => {
                    console.error("error: failed to get link profile", err);
                    const buttons = record.buttons || [];
                    setLinkButtons(buttons);
                    setEditingProfile(record);
                    setIsMainLink(record.path === "");
                    form.setFieldsValue(record);
                    // Initialize button form from backend data
                    setTimeout(() => {
                      initializeFormsFromButtons(buttons);
                    }, 0);
                  })
                  .finally(() => {
                    setIsAdd(false);
                    setEditModalVisible(true);
                  });
              }}
            >
              Edit
            </Button>
          <Button
            type="link"
            disabled={!record.active}
            icon={<EyeOutlined />}
            onClick={() => {
              handlePreviewLink(record);
            }}
          >
            Preview
          </Button>
          <Button
            disabled={record.path === ""}
            type="link"
            icon={<CopyOutlined />}
            onClick={() => {
              handleDuplicateProfile(record);
            }}
          >
            Duplicate
          </Button>
          {/* <Button
            type="link"
            onClick={() => {
              getLinkProfile(record.id.toString())
          .then((profile) => {
            setEditingProfile(profile);
            setIsMainLink(profile.path === "");
            form.setFieldsValue(profile);
          })
          .catch((err) => {
            console.error("error: failed to get link profile", err);
            setEditingProfile(record);
            setIsMainLink(record.path === "");
            form.setFieldsValue(record);
          })
          .finally(() => {
            setIsAdd(false);
            setEditModalVisible(true);
          });
            }}
          >
            Edit
          </Button> */}
          <Popconfirm
            title="Are you sure you want to delete this link?"
            onConfirm={() => handleDeleteLink(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <AdminHeader>
      <HorizontalContainer>
        <MainFrame orientation={getOrientation()}>
          <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div
              className="px-4 py-6 sm:px-0"
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <h1 className="text-2xl font-semibold text-gray-900">
                Manage Links
              </h1>

              {waitingForStore ? (
                <div className="flex justify-center py-12">
                  <Spin size="large" />
                </div>
              ) : (
                <div>
                  <Table
                    rowKey="id"
                    loading={loading}
                    columns={columnsMain}
                    dataSource={linkProfiles.filter(
                      (profile) => profile.path === ""
                    )}
                    style={{ maxWidth: "100%" }}
                    pagination={false}
                  />

                  <div
                    className={`mt-4 mb-4 gap-2 ${
                      vertical
                        ? "flex items-start"
                        : "flex justify-end items-center"
                    }`}
                    style={{ gap: 10 }}
                  >
                    <Button
                      type="primary"
                      disabled={!canAddLink}
                      onClick={() => {
                        if (!canAddLink) {
                          message.warning(
                            `Your plan allows up to ${formatLimitValue(
                              limits?.max_link_profiles
                            )} review video page(s). Upgrade to create more.`
                          );
                          return;
                        }
                        setLinkButtons([]);
                        setEditingProfile({} as LinkProfile);
                        form.resetFields();
                        buttonform.resetFields();
                        // Set default colors for all buttons
                        const defaultTextColor = colorToHex(defaultButtonStyles.color);
                        const defaultBgColor = colorToHex(defaultButtonStyles.backgroundColor);
                        form.setFieldsValue({
                          button_1_text_color: defaultTextColor,
                          button_1_background_color: defaultBgColor,
                        });
                        buttonform.setFieldsValue({
                          button_2_text_color: defaultTextColor,
                          button_2_background_color: defaultBgColor,
                          button_3_text_color: defaultTextColor,
                          button_3_background_color: defaultBgColor,
                          button_4_text_color: defaultTextColor,
                          button_4_background_color: defaultBgColor,
                        });
                        setIsAdd(true);
                        setEditModalVisible(true);
                      }}
                    >
                      Add Link
                    </Button>

                    {!canAddLink && (
                      <RouterLink
                        to="/admin/subscription"
                        className="text-sm text-blue-600 hover:text-blue-800 self-center"
                      >
                        Upgrade to add more →
                      </RouterLink>
                    )}

                    <Button type="primary" onClick={downloadAllQRCodes}>
                      Download All QR Codes
                    </Button>
                  </div>

                  <Table
                    loading={loading}
                    rowKey="id"
                    columns={columns}
                    dataSource={linkProfiles.filter(
                      (profile) => profile.path !== ""
                    )}
                    pagination={{
                      current: page,
                      pageSize: pageSize,
                      total: linkProfiles?.length || 0,
                      showSizeChanger: false,
                      showQuickJumper: false,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    scroll={{
                      x: "max-content",
                      scrollToFirstRowOnChange: false,
                    }}
                    style={{ maxWidth: "100%" }}
                    ref={tblRef}
                    onChange={(pagination) => {
                      setPage(pagination.current || 1);
                      // Scroll to bottom when page changes
                      setTimeout(() => {
                        if (tblRef.current) {
                          tblRef.current.scrollTo({ index: -1 });
                        }
                      }, 100);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <Modal
            title={(isAdd ? "Add" : "Edit") + " Link Profile"}
            open={isEditModalVisible}
            onOk={handleSubmit}
            onCancel={() => {
              setEditModalVisible(false);
            }}
            maskClosable={false}
            width="100%"
            style={{ maxWidth: 800 }}
            okButtonProps={{ disabled: !isFormValid || !isButtonFormValid }}
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={editingProfile}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0 items-start"
              onValuesChange={(changedValues, allValues) => {
                const nextProfile: LinkProfile = {
                  ...editingProfile,
                  ...allValues,
                };
                if ("button_1_text_color" in changedValues) {
                  nextProfile.text_color = changedValues.button_1_text_color;
                }
                if ("button_1_background_color" in changedValues) {
                  nextProfile.background_color =
                    changedValues.button_1_background_color;
                }
                setEditingProfile(nextProfile);
              }}
              onFieldsChange={(_, allFields) => {
                const hasError = form
                  .getFieldsError()
                  .some(({ errors }) => errors.length);
                setIsFormValid(!hasError);
              }}
            >
              <Form.Item
                label="Path"
                name="path"
                rules={[{ required: false, message: "Please input the path!" }]}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                  {/* <Input
                    id="edit-path-prefix"
                    prefix={`https://vioo.vip/${selectedStore?.label}/`}
                    disabled
                  /> */}
                  <span className="text-gray-500 break-all">{`${BASE_URL}/${selectedStore?.name}/`}</span>
                  <Input id="edit-path" disabled={isMainLink} />
                </div>
          
              </Form.Item>

              <Form.Item
                label={
                  <span className="inline-flex items-center gap-2">
                    Video URL
                    <br />
                    Step 1: Click the TikTok icon to open TikTok
                    <br />
                    Step 2: Find your video and copy the link
                    <br />
                    Step 3: Paste the link into the input below
                    <br />
                    <a
                      href="https://www.tiktok.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open TikTok to find your video link"
                      aria-label="Open TikTok home page"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "#000",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img src="/img/icons/tiktok.png" alt="TikTok" style={{ marginLeft: 10, width: 48, height: 48 }} />
                    </a>
                    {/* <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setTiktokHintVisible(true);
                      }}
                      className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 text-sm font-normal border-0 bg-transparent p-0 cursor-pointer"
                      aria-label="How to get a TikTok video link"
                    >
                      <QuestionCircleOutlined />
                      How to get link
                    </button> */}
                  </span>
                }
                name="video_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the Video link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input
                  id="edit-video-url"
                  placeholder="Paste TikTok share link here"
                />
              </Form.Item>

              <Form.Item label="Page Title(Optional)" name="title">
                <Input id="edit-title" />
              </Form.Item>

              <Form.Item className="!hidden md:!block" />

              <Form.Item label="Button Layout" name="button_layout">
                <PictureButtonGroup
                  value={editingProfile.button_layout}
                  onChange={(val) => {
                    setEditingProfile({
                      ...editingProfile,
                      button_layout: val,
                    });
                    form.setFieldsValue({ button_layout: val });
                    // Rebuild buttons when layout changes
                    rebuildLinkButtons(val);
                    // buttonform.resetFields();

                    setTimeout(() => {
                      // Trigger error check of buttonForm after layout changes
                      buttonform.validateFields().then(() => {
                        const hasError = buttonform
                          .getFieldsError()
                          .some(({ errors }) => errors.length);
                        setIsButtonFormValid(!hasError);
                      }).catch(() => {
                        setIsButtonFormValid(false);
                      });
                    }, 0);
                  }}
                />
              </Form.Item>

              <Form.Item className="!hidden md:!block" />

              <Form.Item label="Button 1 Text" name="guide_text"
              rules={[
                {
                  required: true,
                  message: "Please input the Button 1 text!",
                },
              ]}
              >
                <Input id="edit-guide-text" 
                  maxLength={32}
                />
              </Form.Item>

              <Form.Item
                label="Button 1 URL"
                name="guide_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the Button 1 link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input id="edit-guide-url" />
              </Form.Item>
              <Form.Item>
                <div style={{ display: "flex", gap: "12px" }}>
                  {renderColorPickerWithApply(
                    "button_1_text_color",
                    "Text Color",
                    "edit-button-1-text-color",
                    "text",
                    "main"
                  )}
                  {renderColorPickerWithApply(
                    "button_1_background_color",
                    "Background Color",
                    "edit-button-1-background-color",
                    "background",
                    "main"
                  )}
                </div>
              </Form.Item>
              <Form.Item className="!hidden md:!block" />
            </Form>

            {/* Button Form */}
            <Form
              form={buttonform}
              layout="vertical"
              className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0 items-start"
              onValuesChange={() => {
                rebuildLinkButtons(editingProfile.button_layout);
              }}

              onFieldsChange={(_, allFields) => {
                const hasError = buttonform
                  .getFieldsError()
                  .some(({ errors }) => errors.length);
                setIsButtonFormValid(!hasError);
              }}
            >
              {editingProfile.button_layout >= 1 ? (
                <>
                  <Form.Item label="Button 2 Text" name="button_2_text"
                  rules={[
                    {
                      required: true,
                      message: "Please input the Button 2 text!",
                    },
                  ]}
                  >
                    <Input id="edit-guide-text-2" 
                    maxLength={32}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Button 2 URL"
                    name="button_2_link"
                    rules={[
                      {
                        required: true,
                        message: "Please input the Button 2 link URL!",
                      },
                      {
                        type: "url",
                        message: "Please enter a valid URL!",
                      },
                    ]}
                  >
                    <Input id="edit-guide-url-2" />
                  </Form.Item>
                  <Form.Item>
                    <div style={{ display: "flex", gap: "12px" }}>
                      {renderColorPickerWithApply(
                        "button_2_text_color",
                        "Text Color",
                        "edit-button-2-text-color",
                        "text",
                        "button"
                      )}
                      {renderColorPickerWithApply(
                        "button_2_background_color",
                        "Background Color",
                        "edit-button-2-background-color",
                        "background",
                        "button"
                      )}
                    </div>
                  </Form.Item>
                  <Form.Item className="!hidden md:!block" />
                </>
              ) : (
                <></>
              )}

              {editingProfile.button_layout >= 3 ? (
                <>
                  <Form.Item label="Button 3 Text" name="button_3_text"
                  rules={[
                    {
                      required: true,
                      message: "Please input the Button 3 text!",
                    },
                  ]}
                  >
                    <Input id="edit-guide-text" 
                    maxLength={32}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Button 3 URL"
                    name="button_3_link"
                    rules={[
                      {
                        required: true,
                        message: "Please input the Button 3 link URL!",
                      },
                      {
                        type: "url",
                        message: "Please enter a valid URL!",
                      },
                    ]}
                  >
                    <Input id="edit-guide-url-3" />
                  </Form.Item>
                  <Form.Item>
                    <div style={{ display: "flex", gap: "12px" }}>
                      {renderColorPickerWithApply(
                        "button_3_text_color",
                        "Text Color",
                        "edit-button-3-text-color",
                        "text",
                        "button"
                      )}
                      {renderColorPickerWithApply(
                        "button_3_background_color",
                        "Background Color",
                        "edit-button-3-background-color",
                        "background",
                        "button"
                      )}
                    </div>
                  </Form.Item>
                  <Form.Item className="!hidden md:!block" />
                </>
              ) : (
                <></>
              )}

              {editingProfile.button_layout >= 4 ? (
                <>
                  <Form.Item label="Button 4 Text" name="button_4_text"
                  rules={[
                    {
                      required: true,
                      message: "Please input the Button 4 text!",
                    },
                  ]}
                  >
                    <Input id="edit-guide-text" 
                    maxLength={32}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Button 4 URL"
                    name="button_4_link"
                    rules={[
                      {
                        required: true,
                        message: "Please input the Button 4 link URL!",
                      },
                      {
                        type: "url",
                        message: "Please enter a valid URL!",
                      },
                    ]}
                  >
                    <Input id="edit-guide-url-4" />
                  </Form.Item>
                  <Form.Item>
                    <div style={{ display: "flex", gap: "12px" }}>
                      {renderColorPickerWithApply(
                        "button_4_text_color",
                        "Text Color",
                        "edit-button-4-text-color",
                        "text",
                        "button"
                      )}
                      {renderColorPickerWithApply(
                        "button_4_background_color",
                        "Background Color",
                        "edit-button-4-background-color",
                        "background",
                        "button"
                      )}
                    </div>
                  </Form.Item>
                  <Form.Item className="!hidden md:!block" />
                </>
              ) : (
                <></>
              )}

              {/* <Form.Item label="About Text" name="about_text">
                <Input id="edit-about-text" />
              </Form.Item>
              <Form.Item
                label="About URL"
                name="about_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the About link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input id="edit-about-url" />
              </Form.Item> */}

              <Form.Item
                label="Link Name"
                name="name"
                style={{ display: "none" }}
              >
                <Input id="edit-name" />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="How to get a TikTok video link"
            open={tiktokHintVisible}
            onCancel={() => setTiktokHintVisible(false)}
            footer={[
              <Button
                key="got-it"
                type="primary"
                onClick={() => setTiktokHintVisible(false)}
              >
                Got it
              </Button>,
            ]}
            width={480}
          >
            <div className="flex items-start gap-3 mb-4">
              <img
                src="/img/icons/tiktok.png"
                alt="TikTok"
                className="w-10 h-10 object-contain shrink-0"
              />
              <p className="text-gray-600 m-0 pt-1">
                Copy the share link from TikTok, then paste it into the Video URL
                field.
              </p>
            </div>
            <ol className="list-decimal pl-5 space-y-3 text-gray-800 m-0">
              <li>
                Open the video in the <strong>TikTok</strong> app or on{" "}
                <a
                  className="text-blue-500 hover:text-blue-600 font-bold"
                  href="https://www.tiktok.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  tiktok.com
                </a>
                .
              </li>
              <li>
                Tap the <strong>Share</strong> button (arrow icon).
              </li>
              <li>
                Tap <strong>Copy link</strong>.
              </li>
              <li>
                Return here and <strong>paste</strong> the link into the Video URL
                input.
              </li>
            </ol>
            <p className="text-gray-500 text-sm mt-4 mb-0">
              Short links like <code>vm.tiktok.com/...</code> or full video URLs
              both work.
            </p>
          </Modal>

          <Modal
            title="QR Code"
            open={qrModalVisible}
            onCancel={() => setQrModalVisible(false)}
            maskClosable={false}
            footer={[
              <Button
                key="download"
                type="primary"
                onClick={() => {
                  const svg = document.querySelector(".qr-code-svg");
                  if (svg) {
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const img = new Image();
                    img.onload = () => {
                      // Set canvas size to accommodate QR code and text
                      const padding = 80;
                      const textHeight = 60;
                      canvas.width = img.width + padding * 2;
                      canvas.height = img.height + padding * 2 + textHeight;

                      // Fill white background
                      if (ctx) {
                        ctx.fillStyle = "#ffffff";
                        ctx.fillRect(0, 0, canvas.width, canvas.height);

                        // Draw QR code
                        ctx.drawImage(img, padding, padding);

                        // Draw URL text
                        ctx.fillStyle = "#333333";
                        ctx.font = "48px Arial";
                        ctx.textAlign = "center";

                        // Split URL into multiple lines if too long
                        const maxWidthModal = canvas.width - padding * 2;
                        const words = qrCodeUrl.split("/");
                        let line = "";
                        let y = img.height + padding * 2 + 30;

                        for (const word of words) {
                          const testLine = line + word + "/";
                          const metrics = ctx.measureText(testLine);

                          if (metrics.width > maxWidthModal && line !== "") {
                            ctx.fillText(line, canvas.width / 2, y);
                            line = word + "/";
                            y += 30;
                          } else {
                            line = testLine;
                          }
                        }
                        ctx.fillText(line, canvas.width / 2, y);
                      }

                      const pngFile = canvas.toDataURL("image/png");
                      const downloadLink = document.createElement("a");
                      downloadLink.download = `${qrCodeUrl}.png`;
                      downloadLink.href = pngFile;
                      downloadLink.click();
                    };
                    img.src = "data:image/svg+xml;base64," + btoa(svgData);
                  }
                }}
              >
                Download QR Code
              </Button>,
              <Button key="close" onClick={() => setQrModalVisible(false)}>
                Close
              </Button>,
            ]}
          >
            <div className="flex justify-center p-4">
              <QRCodeSVG
                style={{ width: "512px", height: "512px" }}
                value={qrCodeUrl}
                size={qrCodeSize}
                className="qr-code-svg"
              />
            </div>
            <div className="text-center mt-4">
              <a href={qrCodeUrl} className="text-gray-600 break-all underline">
                {qrCodeUrl}
              </a>
            </div>
          </Modal>
        </MainFrame>

        {showPageEditor && (
          <PhoneMockup orientation={getOrientation()}>
            <div className="screen" style={{ position: "relative" }}>
              <CloseCircleFilled
                style={{
                  position: "absolute",
                  top: 10,
                  right: 40,
                  zIndex: 10,
                  width: 48,
                  height: 48,
                  fontSize: 32,
                  cursor: "pointer",
                  borderRadius: "50%",
                  border: "none",
                  background: "transparent",
                  color: "#FFF",
                }}
                onClick={() => setShowPageEditor(false)}
                aria-label="Close"
              />
              <PageEditor profileIn={previewProfile} />
            </div>
          </PhoneMockup>
        )}
      </HorizontalContainer>
    </AdminHeader>
  );
};

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 80vh;
`;

const MainFrame = styled.div<{ orientation: string }>`
  width: ${(props) => (props.orientation === "vertical" ? "100%" : "70%")};
  flex-direction: column;
  justify-content: center;
`;

const PhoneMockup = styled.div<{ orientation: string }>`
  width: ${(props) => (props.orientation === "vertical" ? "100%" : "430px")};
  position: ${(props) =>
    props.orientation === "vertical" ? "absolute" : "relative"};
  margin-top: 80px;
  padding: 1rem;
  max-height: 1000px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 30px;
    border: 12px solid #000;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    pointer-events: none;
    z-index: 1;
  }

  .screen {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    max-height: 970px;
    background: #fff;
    border-radius: 18px;
    overflow: hidden; /* Disable scroll */
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 70vh;
  }
`;

// SVG data URLs for layout previews
const layoutImages = [
  // Layout 0: Single button
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='26' viewBox='0 0 80 26'%3E%3Crect x='0' y='0' width='80' height='26' rx='8' fill='%23f0f0f0' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='40' y='18' font-family='Arial' font-size='12' text-anchor='middle' fill='%23333'%3E1%3C/text%3E%3C/svg%3E",
  
  // Layout 1: Two buttons side by side
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='26' viewBox='0 0 88 26'%3E%3Crect x='0' y='0' width='40' height='26' rx='8' fill='%23e6f7ff' stroke='%231890ff' stroke-width='1'/%3E%3Ctext x='20' y='18' font-family='Arial' font-size='12' text-anchor='middle' fill='%231890ff'%3E1%3C/text%3E%3Crect x='48' y='0' width='40' height='26' rx='8' fill='%23f0f0f0' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='68' y='18' font-family='Arial' font-size='12' text-anchor='middle' fill='%23333'%3E2%3C/text%3E%3C/svg%3E",
  
  // Layout 2: Two buttons stacked
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='56' viewBox='0 0 80 56'%3E%3Crect x='0' y='0' width='80' height='26' rx='8' fill='%23e6f7ff' stroke='%231890ff' stroke-width='1'/%3E%3Ctext x='40' y='18' font-family='Arial' font-size='12' text-anchor='middle' fill='%231890ff'%3E1%3C/text%3E%3Crect x='0' y='30' width='80' height='26' rx='8' fill='%23f0f0f0' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='40' y='48' font-family='Arial' font-size='12' text-anchor='middle' fill='%23333'%3E2%3C/text%3E%3C/svg%3E",
  
  // Layout 3: Two buttons on top, one button below
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='56' viewBox='0 0 88 56'%3E%3Crect x='0' y='0' width='40' height='26' rx='8' fill='%23e6f7ff' stroke='%231890ff' stroke-width='1'/%3E%3Ctext x='20' y='18' font-family='Arial' font-size='12' text-anchor='middle' fill='%231890ff'%3E1%3C/text%3E%3Crect x='48' y='0' width='40' height='26' rx='8' fill='%23f0f0f0' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='68' y='18' font-family='Arial' font-size='12' text-anchor='middle' fill='%23333'%3E2%3C/text%3E%3Crect x='0' y='30' width='80' height='26' rx='8' fill='%23f0f0f0' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='40' y='48' font-family='Arial' font-size='12' text-anchor='middle' fill='%23333'%3E3%3C/text%3E%3C/svg%3E",
  
  // Layout 4: Two buttons on top, two buttons below
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='56' viewBox='0 0 88 56'%3E%3Crect x='0' y='0' width='40' height='26' rx='8' fill='%23e6f7ff' stroke='%231890ff' stroke-width='1'/%3E%3Ctext x='20' y='18' font-family='Arial' font-size='12' text-anchor='middle' fill='%231890ff'%3E1%3C/text%3E%3Crect x='48' y='0' width='40' height='26' rx='8' fill='%23f0f0f0' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='68' y='18' font-family='Arial' font-size='12' text-anchor='middle' fill='%23333'%3E2%3C/text%3E%3Crect x='0' y='30' width='40' height='26' rx='8' fill='%23f0f0f0' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='20' y='48' font-family='Arial' font-size='12' text-anchor='middle' fill='%23333'%3E3%3C/text%3E%3Crect x='48' y='30' width='40' height='26' rx='8' fill='%23f0f0f0' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='68' y='48' font-family='Arial' font-size='12' text-anchor='middle' fill='%23333'%3E4%3C/text%3E%3C/svg%3E",
];

function PictureButtonGroup({
  value,
  onChange,
}: {
  value: number;
  onChange: (val: number) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 md:flex md:flex-wrap md:gap-6">
      {layoutImages.map((imgSrc, idx) => (
        <div
          key={idx}
          className="flex items-center justify-center cursor-pointer rounded-lg p-2 min-h-10 md:min-w-[120px]"
          style={{
            border: value === idx ? "2px solid #1890ff" : "1px solid #ccc",
            background: value === idx ? "#e6f7ff" : "#fff",
          }}
          onClick={() => onChange(idx)}
        >
          <img
            src={imgSrc}
            alt={`Layout ${idx + 1}`}
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </div>
      ))}
    </div>
  );
}

export default LinksManager;
